import {
  useQuery,
  useMutation,
  // useQueryClient,
  type QueryKey,
  useQueryClient
} from "@tanstack/react-query";
import { ERRORS } from "../consts";
import { fetcherFactory } from "../services";
import { useAuthStore } from "../stores";
import type { RunsticResponse, UserGoals, UserGoalsConfigs } from "../types";
import { getEnvironmentVariables } from "../utils/general";

const { basePath } = getEnvironmentVariables();

export function useLearnerProgressQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const initiativeId = useAuthStore(state => state.userData?.initiative_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const userId = useAuthStore(state => state.userData?.id_user) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const setUserAttributes = useAuthStore(state => state.setUserAttributes);
  const isLoading = useAuthStore(state => state.isLoading);
  console.log("DEBUG: isSeedingSession useLearnerProgressQuery: ",isSeedingSession);
  const fetcher = fetcherFactory();
  const uri = `/runtastic/learnerProgress/${corporateId}/${userId}?initiativeId=${initiativeId}`;
  console.log("DEBUG: isSeedingSession useLearnerProgressQuery enabled: ",Boolean(enabled && userId && accessToken && sessionToken && corporateId && !isLoading) &&
    !isSeedingSession);
  return useQuery<RunsticResponse>({
    enabled:
      Boolean(enabled && userId && accessToken && sessionToken && corporateId && !isLoading && initiativeId) &&
      !isSeedingSession,
    queryKey: ["userProgress", userId, corporateId, sessionToken],
    queryFn: async () => {
      try {
        console.log("DEBUG: Call Runtastic learnerProgress");
        const res = await fetcher(`${basePath}${uri}`, {
          headers: {
            Authorization: accessToken,
            "x-ada-session-token": sessionToken
          }
        });
        const data = await res.json();
        setUserAttributes(data.attributes);
        return data;
      } catch (error) {
        if (error === ERRORS.UNAUTHORIZED) {
          setUnauthorized(true);
        }
        throw error;
      }
    },
    refetchOnMount: true,
    staleTime: 3 * 60 * 1000
  });
}

export function useGetLearnerGoalsQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const userId = useAuthStore(state => state.userData?.id_user) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);

  const fetcher = fetcherFactory();
  const uri = `/runtastic/learnerGoals/${corporateId}/${userId}`;

  return useQuery<
      unknown,
      unknown,
      UserGoals,
      QueryKey
    >({
      enabled: Boolean(
        enabled
        && userId
        && accessToken
        && sessionToken
        && corporateId
      ) && !isSeedingSession,
      queryFn: () => fetcher(
        `${basePath}${uri}`,
        {
          headers: {
            "Authorization": accessToken,
            "x-ada-session-token": sessionToken
          },
          method: "GET"
        })
        .then((res) => {
          return res.json();
        })
        .then((data: {data: UserGoals}) => {
          return data;

        })
        .catch((error) => {
          if (error === ERRORS.UNAUTHORIZED) {
            setUnauthorized(true);
            throw error; // rethrow so that react query doesn't complain about undefined return value
          } else {
            throw error;
          }
        }),
      queryKey: [accessToken, sessionToken, uri, isSeedingSession, "getUserGoals"],
      refetchOnMount: true,
      staleTime: 3 * 60 * 1000
    });
}

export function useGetLearnerGoalsConfigsQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const userId = useAuthStore(state => state.userData?.id_user) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const isLoading = useAuthStore(state => state.isLoading);

  const fetcher = fetcherFactory();
  const uri = `/runtastic/configs/${corporateId}`;

  return useQuery<
      unknown,
      unknown,
      UserGoalsConfigs,
      QueryKey
    >({
      enabled: Boolean(
        enabled
        && userId
        && accessToken
        && sessionToken
        && corporateId
        && !isLoading
      ) && !isSeedingSession,
      queryFn: () => fetcher(
        `${basePath}${uri}`,
        {
          headers: {
            "Authorization": accessToken,
            "x-ada-session-token": sessionToken
          },
          method: "GET"
        })
        .then((res) => {
          return res.json();
        })
        .then((data: UserGoalsConfigs) => {
          return data;
        })
        .catch((error) => {
          if (error === ERRORS.UNAUTHORIZED) {
            setUnauthorized(true);
            throw error; // rethrow so that react query doesn't complain about undefined return value
          } else {
            throw error;
          }
        }),
      queryKey: [accessToken, sessionToken, uri, isSeedingSession, "getUserGoalsConfigs"],
      refetchOnMount: true,
      staleTime: 3 * 60 * 1000
    });
}

export function usePutLearnerGoalsQuery(plan: string) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const userId = useAuthStore(state => state.userData?.id_user) ?? "";

  const fetcher = fetcherFactory();
  const uri = `/runtastic/learnerGoals/${corporateId}/${userId}`;

  const queryClient = useQueryClient();

  return useMutation({

    mutationFn: () => fetcher(
      `${basePath}${uri}`,
      {
        body: JSON.stringify({
          plan: plan.toUpperCase()
        }),
        headers:{
          "authorization": accessToken,
          "content-type":"application/json",
          "x-ada-session-token": sessionToken
        },
        method: "PUT"
      })
      .catch(async (error) => {
        if (error === ERRORS.UNAUTHORIZED) {
          setUnauthorized(true);
          throw error; // rethrow so that react query doesn't complain about undefined return value
        } else {
          throw await error;
        }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["learnerGoals", userId, corporateId, sessionToken] });
    }
  });
}
